import React, { useState } from 'react';
import { DataTable } from '../components/DataTable';
import { WizardForm } from '../components/WizardForm';
import { StatusBadge } from '../components/StatusBadge';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
// Fix Leaflet default marker icon (use CDN URLs to avoid bundler issues)
L.Marker.prototype.options.icon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});
function MapUpdater({
  center
}: {
  center: [number, number];
}) {
  const map = useMap();
  map.setView(center, map.getZoom());
  return null;
}
export function ForestCompliance() {
  const [activeTab, setActiveTab] = useState<'cases' | 'file' | 'blacklist'>('cases');
  const [currentStep, setCurrentStep] = useState(0);
  // Form State
  const [formData, setFormData] = useState({
    offenderSearch: '',
    offenderName: '',
    offenderDescription: '',
    offenseType: '',
    date: '',
    time: '',
    latitude: '21.9497',
    longitude: '89.1833',
    description: '',
    fineAmount: '',
    suspendPermit: false,
    blacklist: false,
    notes: ''
  });
  const [casesList, setCasesList] = useState([{
    caseId: 'CASE-2024-001',
    offender: 'Unknown Person',
    offense: 'Illegal Entry',
    date: '2024-03-28',
    latitude: 21.9497,
    longitude: 89.1833,
    status: 'open' as const,
    severity: 'High'
  }, {
    caseId: 'CASE-2024-002',
    offender: 'Habib Mollah',
    offense: 'Poaching',
    date: '2024-03-25',
    latitude: 21.8285,
    longitude: 89.5862,
    status: 'resolved' as const,
    severity: 'Critical'
  }, {
    caseId: 'CASE-2024-003',
    offender: 'Nur Islam',
    offense: 'Overharvesting',
    date: '2024-03-20',
    latitude: 22.0174,
    longitude: 89.75,
    status: 'escalated' as const,
    severity: 'Medium'
  }, {
    caseId: 'CASE-2024-004',
    offender: 'Kamal Uddin',
    offense: 'Unauthorized Vessel',
    date: '2024-03-15',
    latitude: 21.8,
    longitude: 89.55,
    status: 'open' as const,
    severity: 'Low'
  }]);
  const [blacklistData, setBlacklistData] = useState([{
    name: 'Habib Mollah',
    nid: '7890123456789',
    reason: 'Repeated Poaching Offenses',
    blacklistedDate: '2024-03-25',
    duration: '2 years',
    caseRef: 'CASE-2024-002'
  }, {
    name: 'Karim Talukdar',
    nid: '8901234567890',
    reason: 'Illegal Timber Trade',
    blacklistedDate: '2024-02-10',
    duration: '5 years',
    caseRef: 'CASE-2024-015'
  }]);
  const caseColumns = [{
    key: 'caseId',
    label: 'Case ID'
  }, {
    key: 'offender',
    label: 'Offender Name'
  }, {
    key: 'offense',
    label: 'Offense Type'
  }, {
    key: 'date',
    label: 'Date'
  }, {
    key: 'latitude',
    label: 'Latitude'
  }, {
    key: 'longitude',
    label: 'Longitude'
  }, {
    key: 'status',
    label: 'Status',
    render: (value: string) => <StatusBadge status={value as any} data-id="element-702" />
  }, {
    key: 'severity',
    label: 'Severity'
  }];
  const blacklistColumns = [{
    key: 'name',
    label: 'Name'
  }, {
    key: 'nid',
    label: 'NID'
  }, {
    key: 'reason',
    label: 'Reason'
  }, {
    key: 'blacklistedDate',
    label: 'Blacklisted Date'
  }, {
    key: 'duration',
    label: 'Duration'
  }, {
    key: 'caseRef',
    label: 'Case Reference'
  }];
  const caseFilters = [{
    key: 'offense',
    label: 'Offense Type',
    options: ['Illegal Entry', 'Poaching', 'Overharvesting', 'Unauthorized Vessel']
  }, {
    key: 'status',
    label: 'Status',
    options: ['Open', 'Resolved', 'Escalated']
  }, {
    key: 'severity',
    label: 'Severity',
    options: ['Low', 'Medium', 'High', 'Critical']
  }];
  const wizardSteps = [{
    title: 'Offender Info',
    description: 'Identify the offender'
  }, {
    title: 'Offense Details',
    description: 'Enter offense information'
  }, {
    title: 'Action Taken',
    description: 'Record actions and penalties'
  }, {
    title: 'Review & Submit',
    description: 'Review all information before submitting'
  }];
  return <div className="space-y-6" data-id="element-703">
      <div data-id="element-704">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-705">
          Forest Case & Compliance
        </h1>
        <p className="text-sm text-gray-600 mt-1" data-id="element-706">
          Track offenses and manage blacklist
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-707">
        <div className="flex gap-4" data-id="element-708">
          <button onClick={() => setActiveTab('cases')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'cases' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-709">
            Case Registry
          </button>
          <button onClick={() => setActiveTab('file')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'file' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-710">
            File New Case
          </button>
          <button onClick={() => setActiveTab('blacklist')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'blacklist' ? 'border-red-600 text-red-600' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-711">
            Blacklist Registry
          </button>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'cases' && <DataTable columns={caseColumns} data={casesList} filters={caseFilters} onDataChange={setCasesList} searchPlaceholder="Search cases..." data-id="element-712" />}

      {activeTab === 'blacklist' && <DataTable columns={blacklistColumns} data={blacklistData} onDataChange={setBlacklistData} searchPlaceholder="Search blacklisted individuals..." data-id="element-713" />}

      {activeTab === 'file' && <WizardForm steps={wizardSteps} currentStep={currentStep} onStepChange={setCurrentStep} onSubmit={() => {
      if (!formData.offenseType || !formData.date) {
        alert('Please fill in required fields.');
        return;
      }
      const newCase = {
        caseId: `CASE-2024-${String(casesList.length + 1).padStart(3, '0')}`,
        offender: formData.offenderName || 'Unknown Person',
        offense: formData.offenseType,
        date: formData.date,
        latitude: parseFloat(formData.latitude) || 21.9497,
        longitude: parseFloat(formData.longitude) || 89.1833,
        status: 'open' as const,
        severity: 'Medium'
      };
      setCasesList([newCase, ...casesList]);
      alert('Case filed successfully!');
      setCurrentStep(0);
      setActiveTab('cases');
      // Reset form
      setFormData({
        offenderSearch: '',
        offenderName: '',
        offenderDescription: '',
        offenseType: '',
        date: '',
        time: '',
        latitude: '21.9497',
        longitude: '89.1833',
        description: '',
        fineAmount: '',
        suspendPermit: false,
        blacklist: false,
        notes: ''
      });
    }} data-id="element-714">
          {currentStep === 0 && <div className="space-y-4" data-id="element-715">
              <div data-id="element-716">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-717">
                  Search Beneficiary
                </label>
                <input type="text" value={formData.offenderSearch} onChange={e => setFormData({
            ...formData,
            offenderSearch: e.target.value
          })} placeholder="Enter NID or Name (leave empty if unknown)" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-718" />
              </div>
              <div className="text-center text-sm text-gray-600 py-2" data-id="element-719">
                OR Enter Unknown Person Details
              </div>
              <div className="space-y-4" data-id="element-720">
                <div data-id="element-721">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-722">
                    Name (if known)
                  </label>
                  <input type="text" value={formData.offenderName} onChange={e => setFormData({
              ...formData,
              offenderName: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-723" />
                </div>
                <div data-id="element-724">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-725">
                    Description
                  </label>
                  <textarea rows={3} value={formData.offenderDescription} onChange={e => setFormData({
              ...formData,
              offenderDescription: e.target.value
            })} placeholder="Physical description, clothing, etc." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-726"></textarea>
                </div>
              </div>
            </div>}
          {currentStep === 1 && <div className="space-y-4" data-id="element-727">
              <div data-id="element-728">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-729">
                  Offense Type *
                </label>
                <select value={formData.offenseType} onChange={e => setFormData({
            ...formData,
            offenseType: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-730">
                  <option value="" data-id="element-731">Select Offense</option>
                  <option value="Illegal Entry" data-id="element-732">Illegal Entry</option>
                  <option value="Poaching" data-id="element-733">Poaching</option>
                  <option value="Overharvesting" data-id="element-734">Overharvesting</option>
                  <option value="Unauthorized Vessel" data-id="element-735">
                    Unauthorized Vessel
                  </option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-736">
                <div data-id="element-737">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-738">
                    Date *
                  </label>
                  <input type="date" value={formData.date} onChange={e => setFormData({
              ...formData,
              date: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-739" />
                </div>
                <div data-id="element-740">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-741">
                    Time *
                  </label>
                  <input type="time" value={formData.time} onChange={e => setFormData({
              ...formData,
              time: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-742" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-743">
                <div data-id="element-744">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-745">
                    Latitude *
                  </label>
                  <input type="number" step="0.000001" value={formData.latitude} onChange={e => setFormData({
              ...formData,
              latitude: e.target.value
            })} placeholder="e.g. 21.9497" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-746" />
                </div>
                <div data-id="element-747">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-748">
                    Longitude *
                  </label>
                  <input type="number" step="0.000001" value={formData.longitude} onChange={e => setFormData({
              ...formData,
              longitude: e.target.value
            })} placeholder="e.g. 89.1833" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-749" />
                </div>
              </div>
              <div className="h-[300px] w-full rounded-lg overflow-hidden border border-gray-300 z-0 relative" data-id="element-750">
                <MapContainer center={[parseFloat(formData.latitude) || 21.9497, parseFloat(formData.longitude) || 89.1833]} zoom={10} style={{
            height: '100%',
            width: '100%'
          }} data-id="element-751">
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' data-id="element-752" />
                  <MapUpdater center={[parseFloat(formData.latitude) || 21.9497, parseFloat(formData.longitude) || 89.1833]} data-id="element-753" />
                  <Marker position={[parseFloat(formData.latitude) || 21.9497, parseFloat(formData.longitude) || 89.1833]} data-id="element-754" />
                </MapContainer>
              </div>
              <div data-id="element-755">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-756">
                  Description *
                </label>
                <textarea rows={4} value={formData.description} onChange={e => setFormData({
            ...formData,
            description: e.target.value
          })} placeholder="Detailed description of the offense" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-757"></textarea>
              </div>
              <div data-id="element-758">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-759">
                  Evidence Upload
                </label>
                <input type="file" multiple accept="image/*,video/*" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-760" />
              </div>
            </div>}
          {currentStep === 2 && <div className="space-y-4" data-id="element-761">
              <div data-id="element-762">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-763">
                  Fine Amount (BDT)
                </label>
                <input type="number" value={formData.fineAmount} onChange={e => setFormData({
            ...formData,
            fineAmount: e.target.value
          })} placeholder="0" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-764" />
              </div>
              <div className="space-y-2" data-id="element-765">
                <label className="flex items-center gap-2" data-id="element-766">
                  <input type="checkbox" checked={formData.suspendPermit} onChange={e => setFormData({
              ...formData,
              suspendPermit: e.target.checked
            })} className="w-4 h-4 text-[#1B5E20] rounded" data-id="element-767" />
                  <span className="text-sm font-medium text-gray-700" data-id="element-768">
                    Suspend Permit
                  </span>
                </label>
                <label className="flex items-center gap-2" data-id="element-769">
                  <input type="checkbox" checked={formData.blacklist} onChange={e => setFormData({
              ...formData,
              blacklist: e.target.checked
            })} className="w-4 h-4 text-[#1B5E20] rounded" data-id="element-770" />
                  <span className="text-sm font-medium text-gray-700" data-id="element-771">
                    Add to Blacklist
                  </span>
                </label>
              </div>
              <div data-id="element-772">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-773">
                  Additional Notes
                </label>
                <textarea rows={4} value={formData.notes} onChange={e => setFormData({
            ...formData,
            notes: e.target.value
          })} placeholder="Any additional actions taken or notes" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-774"></textarea>
              </div>
            </div>}
          {currentStep === 3 && <div className="space-y-6" data-id="element-775">
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-776">
                <h4 className="font-semibold text-gray-900 mb-3" data-id="element-777">
                  Offender Information
                </h4>
                <div className="text-sm" data-id="element-778">
                  <div data-id="element-779">
                    <span className="text-gray-600" data-id="element-780">Name:</span>{' '}
                    <span className="font-medium" data-id="element-781">
                      {formData.offenderName || 'Unknown Person'}
                    </span>
                  </div>
                  <div data-id="element-782">
                    <span className="text-gray-600" data-id="element-783">Description:</span>{' '}
                    <span className="font-medium" data-id="element-784">
                      {formData.offenderDescription || 'Not provided'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-785">
                <h4 className="font-semibold text-gray-900 mb-3" data-id="element-786">
                  Offense Details
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-787">
                  <div data-id="element-788">
                    <span className="text-gray-600" data-id="element-789">Type:</span>{' '}
                    <span className="font-medium" data-id="element-790">
                      {formData.offenseType || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-791">
                    <span className="text-gray-600" data-id="element-792">Date:</span>{' '}
                    <span className="font-medium" data-id="element-793">
                      {formData.date || 'Not provided'} {formData.time}
                    </span>
                  </div>
                  <div data-id="element-794">
                    <span className="text-gray-600" data-id="element-795">Latitude:</span>{' '}
                    <span className="font-medium" data-id="element-796">
                      {formData.latitude || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-797">
                    <span className="text-gray-600" data-id="element-798">Longitude:</span>{' '}
                    <span className="font-medium" data-id="element-799">
                      {formData.longitude || 'Not provided'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-800">
                <h4 className="font-semibold text-gray-900 mb-3" data-id="element-801">
                  Action Taken
                </h4>
                <div className="text-sm" data-id="element-802">
                  <div data-id="element-803">
                    <span className="text-gray-600" data-id="element-804">Fine:</span>{' '}
                    <span className="font-medium" data-id="element-805">
                      {formData.fineAmount ? `${formData.fineAmount} BDT` : 'None'}
                    </span>
                  </div>
                  <div data-id="element-806">
                    <span className="text-gray-600" data-id="element-807">Permit Suspended:</span>{' '}
                    <span className="font-medium" data-id="element-808">
                      {formData.suspendPermit ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div data-id="element-809">
                    <span className="text-gray-600" data-id="element-810">Blacklisted:</span>{' '}
                    <span className="font-medium" data-id="element-811">
                      {formData.blacklist ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>
            </div>}
        </WizardForm>}
    </div>;
}